import { useState, useRef, useEffect, useCallback } from "react";
import type { ChangeEvent, FormEvent, MouseEvent } from "react";
import type { GetStaticProps } from "next";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  Mail,
  Clock,
  Shield,
  ChevronDown,
  Bell,
  Target,
  ArrowRight,
  ArrowUpRight,
  Building,
  CheckCircle,
  AlertCircle,
  User,
  RefreshCw,
  FileText,
  Filter,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import Meta from "../../components/Meta";
import { fetchPlanningMapFeedPage } from "@/lib/services/applicationsApiService";
import {
  fetchEmailReports,
  fetchUserSubscriptions,
  subscribeToEmailReport,
  unsubscribeWithToken,
  formatReportSchedule,
  getSubscribeStatusMessage,
  type EmailReportPublic,
  type UserSubscription
} from "@/lib/emailReports";
import { apiFetchAccounts, getEmailFromIdToken } from "@/lib/apiAccounts";
import { createAuthPath, getCurrentAuthReturnTo } from "@/lib/authNavigation";
import { trackCoreJourneyEvent } from "@/lib/coreJourneyAnalytics";
import { trackReportSignupEmailEntered, trackReportSignupEvent } from "@/lib/reportSignupAnalytics";
import { suppressWelcomeBackPrompt } from "@/lib/welcomeBackPromptSuppression";
import { Toaster } from "@/components/ui/toaster";

const REPORT_ARCHIVE_WEEKS = 8;
const DATE_RANGE_SEPARATOR = ' \u2013 ';
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAY_MAP: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

const IMPACT_SCALE = [
  { n: 10, bg: '#B5482E' },
  { n: 9,  bg: '#C25437' },
  { n: 8,  bg: '#A86C36' },
  { n: 7,  bg: '#9A7C2F' },
  { n: 6,  bg: '#8A8030' },
  { n: 5,  bg: '#788030' },
  { n: 4,  bg: '#6B7E37' },
  { n: 3,  bg: '#5E7A3C' },
  { n: 2,  bg: '#527540' },
  { n: 1,  bg: '#487048' },
] as const;

const REPORT_ARCHIVE_BASE_URL = 'https://cityscope-images.s3.eu-north-1.amazonaws.com/email-reports';

interface WeeklyReportCategoryStat {
  name: string;
  count: number;
  percentage: number;
}

interface WeeklyReportStats {
  available: boolean;
  totalApplications: number;
  highImpactApplications: number;
  categoryCount: number;
  topCategory: string | null;
  topCategoryCount: number;
  dateRangeLabel: string;
  generatedAt: string | null;
  categories: WeeklyReportCategoryStat[];
}

interface WeeklyPlanningReportsSignupProps {
  weeklyStats: WeeklyReportStats;
}

const EMPTY_WEEKLY_STATS: WeeklyReportStats = {
  available: false,
  totalApplications: 0,
  highImpactApplications: 0,
  categoryCount: 0,
  topCategory: null,
  topCategoryCount: 0,
  dateRangeLabel: 'Past 7 days',
  generatedAt: null,
  categories: [],
};

const getApplicationActivityDate = (record: Record<string, unknown>): Date | null => {
  const raw = record.scraped_date || record.created_at || record.received_date || record.validated_date;
  if (typeof raw !== 'string' && typeof raw !== 'number') return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatShortDate = (date: Date) =>
  date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

const formatWeeklyRange = (start: Date, end: Date) =>
  `${formatShortDate(start)} - ${formatShortDate(end)}`;

const buildWeeklyStats = (records: Record<string, unknown>[], start: Date, end: Date): WeeklyReportStats => {
  const seen = new Set<string>();
  const weeklyRecords = records.filter((record) => {
    const ref = String(record.reference_no || record.ref || record.id || '').trim();
    if (ref && seen.has(ref)) return false;
    const activityDate = getApplicationActivityDate(record);
    if (!activityDate || activityDate < start || activityDate > end) return false;
    if (ref) seen.add(ref);
    return true;
  });

  const categoryCounts = new Map<string, number>();
  let highImpactApplications = 0;

  weeklyRecords.forEach((record) => {
    const category = typeof record.category === 'string' && record.category.trim()
      ? record.category.trim()
      : 'Uncategorised';
    categoryCounts.set(category, (categoryCounts.get(category) || 0) + 1);

    const scale = Number(record.scale_rating);
    if (Number.isFinite(scale) && scale >= 7) {
      highImpactApplications += 1;
    }
  });

  const categories = Array.from(categoryCounts.entries())
    .map(([name, count]) => ({
      name,
      count,
      percentage: weeklyRecords.length > 0 ? Math.max(8, Math.round((count / weeklyRecords.length) * 100)) : 0,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  const top = categories[0] || null;

  return {
    available: weeklyRecords.length > 0,
    totalApplications: weeklyRecords.length,
    highImpactApplications,
    categoryCount: categoryCounts.size,
    topCategory: top?.name ?? null,
    topCategoryCount: top?.count ?? 0,
    dateRangeLabel: formatWeeklyRange(start, end),
    generatedAt: end.toISOString(),
    categories,
  };
};

const fetchWeeklyReportStats = async (): Promise<WeeklyReportStats> => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(end.getDate() - 7);

  const records: Record<string, unknown>[] = [];
  let cursor: string | null = null;

  for (let page = 0; page < 8; page += 1) {
    const response = await fetchPlanningMapFeedPage({
      scope: 'newest',
      limit: 250,
      cursor,
      timeoutMs: 20000,
      retries: 1,
    });

    records.push(...response.applications);

    const oldestDate = response.applications
      .map(getApplicationActivityDate)
      .filter((date): date is Date => Boolean(date))
      .sort((a, b) => a.getTime() - b.getTime())[0];

    if (!response.next_cursor || (oldestDate && oldestDate < start)) {
      break;
    }

    cursor = response.next_cursor;
  }

  return buildWeeklyStats(records, start, end);
};

export const getStaticProps: GetStaticProps<WeeklyPlanningReportsSignupProps> = async () => {
  try {
    return {
      props: {
        weeklyStats: await fetchWeeklyReportStats(),
      },
      revalidate: 3600,
    };
  } catch (error) {
    console.error('Failed to build weekly report stats', error);
    const end = new Date();
    const start = new Date(end);
    start.setDate(end.getDate() - 7);

    return {
      props: {
        weeklyStats: {
          ...EMPTY_WEEKLY_STATS,
          dateRangeLabel: formatWeeklyRange(start, end),
        },
      },
      revalidate: 900,
    };
  }
};

function readCampaignSourceFromLocation(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const raw = params.get('src') || params.get('source');
  if (!raw) {
    return null;
  }

  const cleaned = raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);

  return cleaned || null;
}

function isLikelySignupEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function reportSignupSource(campaignSource: string | null, mode: 'public' | 'account'): string {
  return campaignSource || (mode === 'public' ? 'reports_signup_public_page' : 'reports_signup_account_page');
}

function classifySubscribeError(error: unknown): string {
  const message = error instanceof Error ? error.message.toLowerCase() : '';
  if (message.includes('already')) return 'already_subscribed';
  if (message.includes('valid') || message.includes('email')) return 'invalid_email';
  if (message.includes('network') || message.includes('fetch')) return 'network';
  if (message.includes('unauthor') || message.includes('sign in')) return 'auth';
  return 'api_error';
}

// ═══════════════════════════════════════════════════════════
// WEEKLY EMAIL MOCKUP
// Faithful miniature of the actual Monday email — cream
// parchment body inside a "browser" chrome. Rendered alongside
// the live subscribe form in the combined hero block.
// Always cream regardless of theme (it's the actual email).
// ═══════════════════════════════════════════════════════════
const formatCountLabel = (count: number, singular: string, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;

const WeeklyEmailMockup = ({ stats }: { stats: WeeklyReportStats }) => (
  <div className="pr-panel overflow-hidden border-t-2 border-t-[var(--c-text-primary)]">
    <div className="flex items-center justify-between gap-3 border-b border-[var(--c-border)] bg-[var(--c-bg-secondary)] px-4 py-3">
      <span className="pr-data uppercase tracking-[0.14em]">Inbox · Monday 09:00</span>
      <span className="pr-data text-[var(--c-accent)]">New</span>
    </div>

    {/* Cream parchment email body */}
    <div className="bg-[#FBF6E9] text-[#1E1E1E]">
      <div className="flex items-center justify-between gap-3 border-b border-[#E8DDB8] px-5 py-4">
        <div className="flex items-center gap-2.5">
          <div className="grid h-7 w-7 place-items-center rounded-full border border-[#D3AF37] bg-[#FBF6E9]">
            <span className="font-['Space_Grotesk'] text-[12px] font-bold text-[#9A7C1F]">S</span>
          </div>
          <span className="font-['Space_Grotesk'] text-sm font-bold text-[#1E1E1E]">City Scope</span>
        </div>
        <span className="font-['Space_Mono'] text-[10px] tracking-[0.06em] text-[#6B6359]">
          {stats.available ? stats.dateRangeLabel : 'Figures unavailable'}
        </span>
      </div>

      <div className="px-5 pb-5 pt-5">
        <div className="mb-3 h-[3px] w-12 bg-[#D3AF37]" />

        <span className="inline-block rounded-sm bg-[#D3AF37] px-2 py-0.5 font-['Space_Mono'] text-[9px] font-bold uppercase tracking-[0.18em] text-[#1E1E1E]">
          Edinburgh Weekly
        </span>

        <h4 className="mt-3 font-['Space_Grotesk'] text-xl font-bold leading-tight tracking-tight text-[#1E1E1E] sm:text-[22px]">
          Weekly Planning Report (Edinburgh)
        </h4>
        <p className="mt-1 font-['Space_Mono'] text-[10px] font-bold uppercase tracking-[0.22em] text-[#C8841C]">
          Edinburgh
        </p>

        <div className="mt-4 rounded-md border-l-2 border-[#D3AF37] bg-[#F5EDD0] px-4 py-3">
          <p className="text-[12px] leading-relaxed text-[#3a342a]">
            {stats.available
              ? `${formatCountLabel(stats.totalApplications, 'application')} added across Edinburgh in the past week. ${formatCountLabel(stats.highImpactApplications, 'application')} scored 7 or above, with ${stats.topCategory ? stats.topCategory : 'planning categories'} leading the mix.`
              : 'Live weekly figures are not available right now. The report still covers Edinburgh-wide applications, impact scores and source planning records.'}
          </p>
        </div>

        <div className="mt-5 grid grid-cols-3 gap-4">
          <div>
            <p className="font-['Space_Mono'] text-[8.5px] font-bold uppercase tracking-[0.18em] text-[#6B6359]">
              Added
            </p>
            <p className="mt-1 font-['Space_Grotesk'] text-[34px] font-bold leading-none text-[#1E1E1E]">
              {stats.available ? stats.totalApplications : 'N/A'}
            </p>
          </div>
          <div>
            <p className="font-['Space_Mono'] text-[8.5px] font-bold uppercase tracking-[0.18em] text-[#6B6359]">
              7+ score
            </p>
            <p className="mt-1 font-['Space_Grotesk'] text-[34px] font-bold leading-none text-[#9A7C1F]">
              {stats.available ? stats.highImpactApplications : 'N/A'}
            </p>
          </div>
          <div>
            <p className="font-['Space_Mono'] text-[8.5px] font-bold uppercase tracking-[0.18em] text-[#6B6359]">
              Categories
            </p>
            <p className="mt-1 font-['Space_Grotesk'] text-[34px] font-bold leading-none text-[#1E1E1E]">
              {stats.available ? stats.categoryCount : 'N/A'}
            </p>
          </div>
        </div>
        <p className="mt-2 flex items-center gap-1 font-['Space_Mono'] text-[10px] font-bold text-[#B45C2E]">
          <FileText className="h-3 w-3" />
          {stats.available ? `Generated from live feed data at build time` : 'Info not available from the live feed'}
        </p>

        <div className="mt-5 flex items-center justify-between gap-4 rounded-md border border-[#E8DDB8] bg-white/40 px-4 py-3">
          <div className="min-w-0">
            <p className="font-['Space_Mono'] text-[8.5px] font-bold uppercase tracking-[0.2em] text-[#9A7C1F]">
              Top category
            </p>
            <p className="mt-1 truncate font-['Space_Mono'] text-[12px] font-bold text-[#1E1E1E]">
              {stats.available && stats.topCategory ? stats.topCategory : 'Info not available'}
            </p>
          </div>
          <div className="flex-shrink-0 text-right">
            <p className="font-['Space_Mono'] text-[8.5px] font-bold uppercase tracking-[0.2em] text-[#9A7C1F]">
              Count
            </p>
            <p className="mt-1 font-['Space_Grotesk'] text-[18px] font-bold leading-none text-[#B45C2E]">
              {stats.available ? stats.topCategoryCount : 'N/A'}
            </p>
          </div>
        </div>

        <div className="mt-4">
          <p className="mb-1.5 font-['Space_Mono'] text-[8.5px] font-bold uppercase tracking-[0.2em] text-[#6B6359]">
            Impact scale
          </p>
          <div className="flex gap-[3px]">
            {IMPACT_SCALE.map(({ n, bg }) => (
              <div
                key={n}
                className="flex h-6 flex-1 items-center justify-center rounded-[3px] font-['Space_Mono'] text-[10px] font-bold text-white"
                style={{ backgroundColor: bg }}
              >
                {n}
              </div>
            ))}
          </div>
          <div className="mt-1.5 flex items-center justify-between">
            <span className="font-['Space_Mono'] text-[8.5px] uppercase tracking-[0.16em] text-[#6B6359]">Major</span>
            <span className="font-['Space_Mono'] text-[8.5px] uppercase tracking-[0.16em] text-[#6B6359]">Minor</span>
          </div>
        </div>

        <div className="mt-5">
          <p className="font-['Space_Mono'] text-[8.5px] font-bold uppercase tracking-[0.2em] text-[#9A7C1F]">
            Top categories
          </p>
          <div className="mt-2 space-y-1.5">
            {(stats.available && stats.categories.length > 0
              ? stats.categories
              : [{ name: 'Info not available', count: 0, percentage: 100 }]
            ).map((row) => (
              <div key={row.name} className="flex items-center gap-3">
                <span
                  className="w-28 flex-shrink-0 truncate font-['Space_Mono'] text-[10px] text-[#1E1E1E]"
                  title={row.name}
                >
                  {row.name}
                </span>
                <div className="relative h-2 flex-1 rounded-full bg-[#EFE4C0]">
                  <div className="absolute inset-y-0 left-0 rounded-full bg-[#B5482E]" style={{ width: `${row.percentage}%` }} />
                </div>
                <span className="w-24 flex-shrink-0 text-right font-['Space_Mono'] text-[9px] font-bold text-[#1E1E1E]">
                  {stats.available ? formatCountLabel(row.count, 'app') : 'Try later'}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-5 flex items-center justify-center gap-1.5 rounded-md border border-[#D3AF37] bg-[#D3AF37]/10 py-2.5">
          <span className="font-['Space_Mono'] text-[10px] font-bold uppercase tracking-[0.2em] text-[#9A7C1F]">
            View Full Report
          </span>
          <ArrowUpRight className="h-3 w-3 text-[#9A7C1F]" />
        </div>
      </div>
    </div>
  </div>
);

const WeeklyStatsPanel = ({
  stats,
  className = '',
}: {
  stats: WeeklyReportStats;
  className?: string;
}) => (
  <div className={`pr-panel p-5 ${className}`}>
    <p className="pr-section-label">This week</p>
    <dl className="mt-5 grid grid-cols-2 border border-[var(--c-border)] bg-[var(--c-bg-secondary)]">
      <div className="p-3">
        <dt className="pr-data text-[10px] uppercase tracking-[0.14em]">Added</dt>
        <dd className="pr-numeral mt-1 text-3xl">
          {stats.available ? stats.totalApplications : '—'}
        </dd>
      </div>
      <div className="border-l border-[var(--c-border)] p-3">
        <dt className="pr-data text-[10px] uppercase tracking-[0.14em]">High impact</dt>
        <dd className="pr-numeral mt-1 text-3xl">
          {stats.available ? stats.highImpactApplications : '—'}
        </dd>
      </div>
    </dl>
    <p className="pr-data mt-4">Delivery: Monday 09:00</p>
    <p className="pr-data mt-2">
      {stats.available ? stats.dateRangeLabel : 'Live figures unavailable'}
    </p>
  </div>
);

const FeatureCard = ({ icon: Icon, title, description }: {
  icon: LucideIcon;
  title: string;
  description: string;
  delay?: number;
}) => (
  <div className="pr-panel flex h-full flex-col p-5">
    <span className="flex h-10 w-10 items-center justify-center border border-[var(--c-border)] bg-[var(--c-bg-secondary)] text-[var(--c-accent)]">
      <Icon className="h-5 w-5" strokeWidth={1.5} />
    </span>
    <h3 className="mt-5 font-['Newsreader'] text-xl font-medium leading-tight text-[var(--c-text-primary)]">
      {title}
    </h3>
    <p className="mt-3 text-sm leading-6 text-[var(--c-text-secondary)]">
      {description}
    </p>
  </div>
);

// ═══════════════════════════════════════════════════════════
// STEP CARD FOR "HOW IT WORKS"
// ═══════════════════════════════════════════════════════════
const StepCard = ({ number, title, description }: {
  number: number;
  title: string;
  description: string;
  delay?: number;
}) => (
  <div className="flex items-start gap-5 border border-[var(--c-border)] bg-[var(--c-surface)] p-5">
    <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center border border-[var(--c-border)] bg-[var(--c-bg-secondary)] font-['Newsreader'] text-2xl font-medium text-[var(--c-text-primary)]">
      {number}
    </span>
    <div className="min-w-0 pt-1">
      <h3 className="font-['Newsreader'] text-xl font-medium leading-tight text-[var(--c-text-primary)]">
        {title}
      </h3>
      <p className="mt-2 text-sm leading-6 text-[var(--c-text-secondary)]">
        {description}
      </p>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════
// FAQ ACCORDION ITEM
// ═══════════════════════════════════════════════════════════
const FAQItem = ({ question, answer, isOpen, onClick }: {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
}) => (
  <div className="border border-[var(--c-border)] bg-[var(--c-surface)]">
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-[var(--c-surface-hover)]"
      aria-expanded={isOpen}
    >
      <span className="font-['Newsreader'] text-lg font-medium leading-snug text-[var(--c-text-primary)]">
        {question}
      </span>
      <ChevronDown
        className={`h-5 w-5 flex-shrink-0 text-[var(--c-accent)] transition-transform ${isOpen ? 'rotate-180' : ''}`}
        strokeWidth={1.6}
      />
    </button>
    {isOpen ? (
      <div className="border-t border-[var(--c-border)] px-5 py-4 text-sm leading-6 text-[var(--c-text-secondary)]">
        {answer}
      </div>
    ) : null}
  </div>
);

// ═══════════════════════════════════════════════════════════
// SUBSCRIPTION STATE TYPE
// ═══════════════════════════════════════════════════════════
interface SubscriptionState {
  isLoading: boolean;
  isUnsubscribing: boolean;
  isSubscribed: boolean;
  error: string | null;
  statusMessage: string | null;
  unsubscribeToken: string | null;
}
interface DateParts {
  year: number;
  month: number;
  day: number;
}

interface WeeklyReportArchiveItem {
  id: string;
  report_key: string;
  report_title: string;
  date_range: string;
  url: string;
  end_date_key: number;
}

function getZonedParts(date: Date, timeZone: string) {
  let formatter: Intl.DateTimeFormat;
  try {
    formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: timeZone || 'Europe/London',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      weekday: 'short',
      hour12: false,
    });
  } catch {
    formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      weekday: 'short',
      hour12: false,
    });
  }

  const parts = formatter.formatToParts(date);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '';

  return {
    year: Number.parseInt(get('year'), 10) || 0,
    month: Number.parseInt(get('month'), 10) || 0,
    day: Number.parseInt(get('day'), 10) || 0,
    hour: Number.parseInt(get('hour'), 10) || 0,
    minute: Number.parseInt(get('minute'), 10) || 0,
    weekday: get('weekday'),
  };
}

function normalizeWeekdayLabel(label: string): number {
  const key = (label || '').replace(/[^A-Za-z]/g, '').slice(0, 3);
  return WEEKDAY_MAP[key] ?? 0;
}

function toDateKey(parts: DateParts): number {
  return parts.year * 10000 + parts.month * 100 + parts.day;
}

function shiftDate(parts: DateParts, days: number): DateParts {
  const dt = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  dt.setUTCDate(dt.getUTCDate() + days);
  return { year: dt.getUTCFullYear(), month: dt.getUTCMonth() + 1, day: dt.getUTCDate() };
}

function formatDate(parts: DateParts): string {
  const day = String(parts.day).padStart(2, '0');
  const month = MONTH_ABBR[parts.month - 1] ?? 'Jan';
  return `${day} ${month} ${parts.year}`;
}

function formatDateRange(start: DateParts, end: DateParts): string {
  return `${formatDate(start)}${DATE_RANGE_SEPARATOR}${formatDate(end)}`;
}

function getLatestWeeklyEndDate(report: EmailReportPublic, timeZone: string): DateParts {
  const nowParts = getZonedParts(new Date(), timeZone);
  const currentDow = normalizeWeekdayLabel(nowParts.weekday);
  const scheduledDow = typeof report.send_dow === 'number' ? report.send_dow : 1;
  let deltaDays = (currentDow - scheduledDow + 7) % 7;
  const nowMinutes = nowParts.hour * 60 + nowParts.minute;
  const sendMinutes = report.send_hour * 60 + report.send_minute;

  if (deltaDays === 0 && nowMinutes < sendMinutes) {
    deltaDays = 7;
  }

  return shiftDate({ year: nowParts.year, month: nowParts.month, day: nowParts.day }, -deltaDays);
}

function getSubscriptionDateParts(value: string | null | undefined, timeZone: string): DateParts | null {
  if (!value) return null;
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return null;
  const parts = getZonedParts(dt, timeZone);
  return { year: parts.year, month: parts.month, day: parts.day };
}

// Mirror backend report ID hashing for S3 archive URLs.
async function computeReportId(email: string, dateRange: string, reportKey: string): Promise<string> {
  if (typeof crypto === 'undefined' || !crypto.subtle) return '';
  const input = `${email}:${dateRange}:${reportKey}`;
  const hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return hashHex.slice(0, 16);
}

async function buildWeeklyReportArchive(
  userEmail: string,
  reports: EmailReportPublic[],
  subscriptions: UserSubscription[],
  maxWeeks: number = REPORT_ARCHIVE_WEEKS
): Promise<WeeklyReportArchiveItem[]> {
  if (!userEmail || typeof crypto === 'undefined' || !crypto.subtle) return [];

  const email = userEmail.trim().toLowerCase();
  const weeklyReports = reports.filter((report) => report.is_active && report.cadence === 'weekly');
  const items: WeeklyReportArchiveItem[] = [];
  const hasSubscriptionData = subscriptions.length > 0;

  for (const report of weeklyReports) {
    const timeZone = report.timezone || 'Europe/London';
    const subscription = subscriptions.find((sub) => sub.report_key === report.report_key);

    if (hasSubscriptionData && !subscription) {
      continue;
    }

    const earliest = subscription ? getSubscriptionDateParts(subscription.created_at, timeZone) : null;
    const lastSent = subscription ? getSubscriptionDateParts(subscription.last_sent_at, timeZone) : null;

    let endDate = getLatestWeeklyEndDate(report, timeZone);
    if (lastSent && toDateKey(lastSent) < toDateKey(endDate)) {
      endDate = lastSent;
    }

    for (let i = 0; i < maxWeeks; i += 1) {
      const currentEnd = shiftDate(endDate, -7 * i);
      if (earliest && toDateKey(currentEnd) < toDateKey(earliest)) {
        break;
      }

      const start = shiftDate(currentEnd, -7);
      const dateRange = formatDateRange(start, currentEnd);
      const reportId = await computeReportId(email, dateRange, report.report_key);

      if (!reportId) {
        continue;
      }

      items.push({
        id: `${report.report_key}-${reportId}`,
        report_key: report.report_key,
        report_title: report.title,
        date_range: dateRange,
        url: `${REPORT_ARCHIVE_BASE_URL}/${reportId}.html`,
        end_date_key: toDateKey(currentEnd),
      });
    }
  }

  return items.sort((a, b) => b.end_date_key - a.end_date_key);
}

async function filterReachableArchiveItems(items: WeeklyReportArchiveItem[]): Promise<WeeklyReportArchiveItem[]> {
  // Do not verify S3 archive links from the browser. Some valid S3 objects do
  // not allow cross-origin HEAD requests, which would hide real archive links.
  return items;
}

// ═══════════════════════════════════════════════════════════
// COMPACT SUBSCRIBE PANELS (anonymous + logged-in)
// Live in the LEFT side of the combined hero. Theme-aware.
// ═══════════════════════════════════════════════════════════

const PanelHeader = ({ kicker, title, body }: { kicker: string; title: string; body: string }) => (
  <div>
    <p className="pr-kicker pr-kicker--accent">{kicker}</p>
    <h3 className="mt-4 font-['Newsreader'] text-2xl font-medium leading-tight text-[var(--c-text-primary)] sm:text-[26px]">
      {title}
    </h3>
    <p className="mt-3 text-sm leading-relaxed text-[var(--c-text-secondary)] sm:text-[15px]">
      {body}
    </p>
  </div>
);

const PanelTrustLine = () => (
  <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-[var(--c-border)] pt-5">
    <div className="flex items-center gap-1.5">
      <Shield className="h-3.5 w-3.5 text-[var(--c-accent)]" strokeWidth={1.6} />
      <span className="pr-data text-[10px] uppercase tracking-[0.14em]">SSL</span>
    </div>
    <span className="text-[var(--c-text-tertiary)]">·</span>
    <div className="flex items-center gap-1.5">
      <Mail className="h-3.5 w-3.5 text-[var(--c-accent)]" strokeWidth={1.6} />
      <span className="pr-data text-[10px] uppercase tracking-[0.14em]">No spam</span>
    </div>
    <span className="text-[var(--c-text-tertiary)]">·</span>
    <div className="flex items-center gap-1.5">
      <FileText className="h-3.5 w-3.5 text-[var(--c-accent)]" strokeWidth={1.6} />
      <span className="pr-data text-[10px] uppercase tracking-[0.14em]">Weekly brief</span>
    </div>
  </div>
);

const SubscribePanelAnonymous = ({
  publicEmail,
  statusEmail,
  onPublicEmailChange,
  onEmailFocus,
  onEmailEntered,
  onEmailInvalid,
  reports,
  subscriptionStates,
  onSubscribe,
  onCreateAccount,
  onSignIn,
}: {
  publicEmail: string;
  statusEmail: string | null;
  onPublicEmailChange: (value: string) => void;
  onEmailFocus: (report: EmailReportPublic) => void;
  onEmailEntered: (report: EmailReportPublic, email: string) => void;
  onEmailInvalid: (report: EmailReportPublic, email: string) => void;
  reports: EmailReportPublic[];
  subscriptionStates: Record<string, SubscriptionState>;
  onSubscribe: (report: EmailReportPublic, emailOverride?: string) => void;
  onCreateAccount: () => void;
  onSignIn: () => void;
}) => {
  const primaryReport = reports[0];
  const normalized = publicEmail.trim().toLowerCase();
  const liveState: SubscriptionState | null =
    primaryReport && statusEmail === normalized
      ? subscriptionStates[primaryReport.report_key] || null
      : null;
  const isSubscribed = !!liveState?.isSubscribed;
  const isLoading = !!liveState?.isLoading;
  const error = liveState?.error || null;
  const statusMessage = liveState?.statusMessage || null;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!primaryReport) return;
    onSubscribe(primaryReport, publicEmail);
  };

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    onPublicEmailChange(nextValue);
    if (primaryReport) {
      onEmailEntered(primaryReport, nextValue);
    }
  };

  return (
    <>
      <PanelHeader
        kicker="Subscribe — it's free"
        title="Get the brief in your inbox."
        body="Edinburgh planning, summarised. Every Monday at 09:00, in plain English."
      />

      {/* Inline email + subscribe form */}
      <form data-analytics-form="report_signup" onSubmit={handleSubmit} className="mt-6 space-y-3">
        <label
          htmlFor="weekly-report-email"
          className="pr-data text-[10px] uppercase tracking-[0.14em] text-[var(--c-text-tertiary)]"
        >
          Your email
        </label>
        <div className="flex flex-col gap-2.5 sm:flex-row sm:items-stretch">
          <div className="pr-report-signup-field flex min-w-0 flex-1 items-stretch border border-[var(--c-border)] bg-[var(--c-bg)] transition-colors focus-within:border-[var(--c-text-primary)]">
            <input
              id="weekly-report-email"
              type="email"
              required
              value={publicEmail}
              onFocus={() => {
                if (primaryReport) onEmailFocus(primaryReport);
              }}
              onChange={handleEmailChange}
              onInvalid={() => {
                if (primaryReport) onEmailInvalid(primaryReport, publicEmail);
              }}
              placeholder="you@example.com"
              autoComplete="email"
              disabled={isSubscribed}
              className="h-12 min-w-0 flex-1 bg-transparent px-4 text-[15px] text-[var(--c-text-primary)] outline-none placeholder:text-[var(--c-text-tertiary)] disabled:opacity-60"
            />
          </div>
          <button
            type="submit"
            disabled={isLoading || isSubscribed || !primaryReport}
            className="pr-button h-12 px-5 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isLoading ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Subscribing
              </>
            ) : isSubscribed ? (
              <>
                <CheckCircle className="h-4 w-4" />
                Subscribed
              </>
            ) : (
              <>
                <Mail className="h-4 w-4" />
                Subscribe
              </>
            )}
          </button>
        </div>

        {statusMessage ? (
          <div className="flex items-start gap-2 border border-[var(--c-border)] bg-[var(--c-accent-muted)] px-3 py-2.5">
            <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--c-accent)]" />
            <p className="text-[13px] leading-snug text-[var(--c-text-primary)]">{statusMessage}</p>
          </div>
        ) : null}
        {error ? (
          <div className="flex items-start gap-2 border border-[var(--c-error)]/30 bg-[var(--c-error)]/10 px-3 py-2.5">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--c-error)]" />
            <p className="text-[13px] leading-snug text-[var(--c-text-primary)]">{error}</p>
          </div>
        ) : null}

        <p className="text-xs leading-relaxed text-[var(--c-text-tertiary)]">
          We&apos;ll send a confirmation, then your weekly brief every Monday. One-click unsubscribe in every email.
        </p>
      </form>

      <div className="mt-6 border border-[var(--c-border)] bg-[var(--c-bg-secondary)] p-4">
        <p className="pr-data text-[10px] uppercase tracking-[0.14em] text-[var(--c-text-tertiary)]">
          Want archives + saved searches?
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button type="button" onClick={onCreateAccount} className="pr-button pr-button--ghost">
            <User className="h-3.5 w-3.5" />
            Create account
          </button>
          <span className="pr-data text-[10px] uppercase tracking-[0.14em] text-[var(--c-text-tertiary)]">or</span>
          <button type="button" onClick={onSignIn} className="pr-textlink">
            Sign in
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <PanelTrustLine />
    </>
  );
};

const SubscribePanelLoggedIn = ({
  userEmail,
  reports,
  subscriptionStates,
  onSubscribe,
  onUnsubscribe,
}: {
  userEmail: string;
  reports: EmailReportPublic[];
  subscriptionStates: Record<string, SubscriptionState>;
  onSubscribe: (report: EmailReportPublic) => void;
  onUnsubscribe: (reportKey: string, token?: string | null) => void;
}) => {
  const getReportState = (reportKey: string): SubscriptionState =>
    subscriptionStates[reportKey] || {
      isLoading: false,
      isUnsubscribing: false,
      isSubscribed: false,
      error: null,
      statusMessage: null,
      unsubscribeToken: null,
    };

  const allSubscribed = reports.length > 0 && reports.every((r) => getReportState(r.report_key).isSubscribed);

  return (
    <>
      <PanelHeader
        kicker="Your subscription"
        title="You're signed in."
        body="Manage which weekly briefs land in your inbox."
      />

      <div className="mt-5 flex items-center gap-3 border border-[var(--c-border)] bg-[var(--c-bg-secondary)] px-3.5 py-3">
        <div className="grid h-9 w-9 flex-shrink-0 place-items-center border border-[var(--c-border)] bg-[var(--c-surface)] text-[var(--c-accent)]">
          <User className="h-4 w-4" strokeWidth={1.6} />
        </div>
        <div className="min-w-0">
          <p className="pr-data text-[10px] uppercase tracking-[0.14em] text-[var(--c-text-tertiary)]">
            Signed in as
          </p>
          <p className="truncate text-sm font-medium text-[var(--c-text-primary)]">
            {userEmail}
          </p>
        </div>
      </div>

      {/* Reports list */}
      {reports.length === 0 ? (
        <div className="mt-5 border border-[var(--c-border)] bg-[var(--c-bg-secondary)] p-5 text-center">
          <FileText className="mx-auto mb-3 h-7 w-7 text-[var(--c-text-tertiary)]" />
          <p className="text-[13.5px] text-[var(--c-text-secondary)]">
            No email reports are available right now. Check back soon.
          </p>
        </div>
      ) : (
        <div className="mt-5 space-y-3">
          {reports.map((report) => {
            const state = getReportState(report.report_key);
            const schedule = formatReportSchedule(report);

            return (
              <div
                key={report.report_key}
                className="border border-[var(--c-border)] bg-[var(--c-bg-secondary)] p-3.5 sm:p-4 transition-colors hover:border-[var(--c-text-tertiary)]"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h4 className="truncate font-['Newsreader'] text-lg font-medium leading-tight text-[var(--c-text-primary)]">
                      {report.title}
                    </h4>
                    <div className="mt-1 flex items-center gap-1.5 text-[var(--c-text-tertiary)]">
                      <Clock className="h-3 w-3 flex-shrink-0" />
                      <span className="pr-data text-[10px] tracking-wide">{schedule}</span>
                    </div>
                  </div>

                  <div className="flex flex-shrink-0 items-center gap-2">
                    {state.isSubscribed ? (
                      <>
                        <span className="pr-data text-[var(--c-accent)]">
                          Subscribed
                        </span>
                        <button
                          type="button"
                          onClick={() => onUnsubscribe(report.report_key, state.unsubscribeToken)}
                          disabled={state.isUnsubscribing}
                          className="pr-textlink"
                        >
                          {state.isUnsubscribing ? 'Unsubscribing' : 'Unsubscribe'}
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onSubscribe(report)}
                        disabled={state.isLoading}
                        className="pr-button disabled:opacity-60"
                      >
                        {state.isLoading ? (
                          <>
                            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                            Subscribing
                          </>
                        ) : (
                          <>
                            <Mail className="h-3.5 w-3.5" />
                            Subscribe
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {state.statusMessage ? (
                  <div className="mt-2.5 flex items-start gap-2 border border-[var(--c-border)] bg-[var(--c-accent-muted)] px-3 py-2">
                    <CheckCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[var(--c-accent)]" />
                    <p className="text-[12.5px] leading-snug text-[var(--c-text-primary)]">{state.statusMessage}</p>
                  </div>
                ) : null}
                {state.error ? (
                  <div className="mt-2.5 flex items-start gap-2 border border-[var(--c-error)]/30 bg-[var(--c-error)]/10 px-3 py-2">
                    <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[var(--c-error)]" />
                    <p className="text-[12.5px] leading-snug text-[var(--c-text-primary)]">{state.error}</p>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {allSubscribed ? (
        <div className="mt-4 flex items-center gap-2 border border-[var(--c-border)] bg-[var(--c-accent-muted)] px-3.5 py-2.5">
          <CheckCircle className="h-4 w-4 flex-shrink-0 text-[var(--c-accent)]" />
          <p className="text-[13px] text-[var(--c-text-primary)]">
            You&apos;re all set — check your inbox on Monday at 09:00.
          </p>
        </div>
      ) : null}

      <PanelTrustLine />
    </>
  );
};

// ═══════════════════════════════════════════════════════════
// WEEKLY ARCHIVE SECTION (logged-in users with subscriptions)
// Lives in its own dedicated section below the hero.
// ═══════════════════════════════════════════════════════════
const WeeklyArchiveSection = ({
  items,
  loading,
}: {
  items: WeeklyReportArchiveItem[];
  loading: boolean;
}) => (
  <div className="pr-panel p-6 sm:p-8">
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3 border-b border-[var(--c-border)] pb-5">
      <div>
        <p className="pr-section-label">Your archive</p>
        <h2 className="pr-heading mt-4">Past weekly reports</h2>
        <p className="mt-2 text-sm text-[var(--c-text-secondary)]">
          Every brief that&apos;s landed in your inbox — open any week to revisit it.
        </p>
      </div>
      <div className="flex items-center gap-2 pr-data">
        <FileText className="h-4 w-4 text-[var(--c-accent)]" />
        {loading ? 'Loading…' : `${items.length} ${items.length === 1 ? 'report' : 'reports'}`}
      </div>
    </div>

    {loading && items.length === 0 ? (
      <div className="flex items-center justify-center py-10">
        <div className="flex items-center gap-3 text-[var(--c-text-secondary)]">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span className="pr-data">Loading archive…</span>
        </div>
      </div>
    ) : items.length === 0 ? (
      <div className="py-10 text-center">
        <FileText className="mx-auto mb-3 h-8 w-8 text-[var(--c-text-tertiary)]" />
        <p className="text-sm text-[var(--c-text-secondary)]">
          No archived reports yet. Your first one will appear here next Monday.
        </p>
      </div>
    ) : (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item) => (
          <a
            key={`${item.report_key}-${item.end_date_key}`}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center justify-between gap-3 border border-[var(--c-border)] bg-[var(--c-bg-secondary)] p-4 transition-colors hover:border-[var(--c-text-tertiary)] hover:bg-[var(--c-surface-hover)]"
          >
            <div className="min-w-0">
              <p className="truncate pr-data text-[10px] uppercase tracking-[0.14em]">
                {item.report_title}
              </p>
              <p className="mt-1.5 font-['Newsreader'] text-lg font-medium leading-tight text-[var(--c-text-primary)]">
                {item.date_range}
              </p>
            </div>
            <ArrowUpRight className="h-5 w-5 flex-shrink-0 text-[var(--c-text-tertiary)] transition-colors group-hover:text-[var(--c-accent)]" />
          </a>
        ))}
      </div>
    )}
  </div>
);

// ═══════════════════════════════════════════════════════════
// MAIN PAGE COMPONENT
// ═══════════════════════════════════════════════════════════
export default function WeeklyPlanningReportsSignup({
  weeklyStats = EMPTY_WEEKLY_STATS,
}: WeeklyPlanningReportsSignupProps) {
  const router = useRouter();

  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  // Auth and subscription state
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [reports, setReports] = useState<EmailReportPublic[]>([]);
  const [authLoading, setAuthLoading] = useState(true);
  const [publicEmail, setPublicEmail] = useState('');
  const [publicStateEmail, setPublicStateEmail] = useState<string | null>(null);
  const [campaignSource, setCampaignSource] = useState<string | null>(null);
  const [subscriptionStates, setSubscriptionStates] = useState<Record<string, SubscriptionState>>({});
  const [userSubscriptions, setUserSubscriptions] = useState<UserSubscription[]>([]);
  const [weeklyArchiveItems, setWeeklyArchiveItems] = useState<WeeklyReportArchiveItem[]>([]);
  const [weeklyArchiveLoading, setWeeklyArchiveLoading] = useState(false);
  const reportFormViewedRef = useRef(false);
  const reportArchiveViewedRef = useRef(false);
  const emailFocusTrackedRef = useRef<Set<string>>(new Set());
  const emailEnteredTrackedRef = useRef<Set<string>>(new Set());
  const primaryReportKey = reports[0]?.report_key;
  const showHeroSubscription = !authLoading && !!userEmail;

  const handleSignUp = useCallback(async (placement = 'signup_page') => {
    const mode = userEmail ? 'account' : 'public';
    trackReportSignupEvent('report_signup_account_cta_clicked', {
      reportKey: primaryReportKey,
      mode,
      source: reportSignupSource(campaignSource, mode),
      placement,
    });
    router.push(createAuthPath('sign-up', getCurrentAuthReturnTo('/reports/signup')));
  }, [campaignSource, primaryReportKey, router, userEmail]);

  const handleLogin = useCallback(async (placement = 'signup_page') => {
    const mode = userEmail ? 'account' : 'public';
    trackReportSignupEvent('report_signup_signin_clicked', {
      reportKey: primaryReportKey,
      mode,
      source: reportSignupSource(campaignSource, mode),
      placement,
    });
    router.push(createAuthPath('sign-in', getCurrentAuthReturnTo('/reports/signup')));
  }, [campaignSource, primaryReportKey, router, userEmail]);

  useEffect(() => {
    setCampaignSource(readCampaignSourceFromLocation());
  }, []);

  useEffect(() => {
    if (authLoading || reports.length === 0 || reportFormViewedRef.current) return;

    const mode = userEmail ? 'account' : 'public';
    reportFormViewedRef.current = true;
    trackReportSignupEvent('report_signup_form_viewed', {
      reportKey: reports[0]?.report_key,
      mode,
      source: reportSignupSource(campaignSource, mode),
    });
  }, [authLoading, campaignSource, reports, userEmail]);

  const handleEmailFocus = useCallback((report: EmailReportPublic) => {
    const reportKey = report.report_key;
    if (emailFocusTrackedRef.current.has(reportKey)) return;

    emailFocusTrackedRef.current.add(reportKey);
    trackReportSignupEvent('report_signup_email_focused', {
      reportKey,
      mode: 'public',
      source: reportSignupSource(campaignSource, 'public'),
    });
  }, [campaignSource]);

  const handleEmailEntered = useCallback((report: EmailReportPublic, email: string) => {
    const trimmed = email.trim();
    if (!trimmed.includes('@') || trimmed.length < 6) return;

    const reportKey = report.report_key;
    if (emailEnteredTrackedRef.current.has(reportKey)) return;

    emailEnteredTrackedRef.current.add(reportKey);
    trackReportSignupEmailEntered(trimmed, {
      reportKey,
      mode: 'public',
      source: reportSignupSource(campaignSource, 'public'),
    });
  }, [campaignSource]);

  const handleEmailInvalid = useCallback((report: EmailReportPublic, email: string) => {
    const source = reportSignupSource(campaignSource, 'public');
    trackReportSignupEvent('report_signup_submit_attempted', {
      reportKey: report.report_key,
      mode: 'public',
      source,
    });
    trackReportSignupEvent('report_signup_validation_failed', {
      reportKey: report.report_key,
      mode: 'public',
      source,
      reason: email.trim() ? 'invalid_email' : 'missing_email',
    });
  }, [campaignSource]);

  const handleFinalSubscribeCta = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const mode = userEmail ? 'account' : 'public';
    trackReportSignupEvent('report_signup_final_cta_clicked', {
      reportKey: primaryReportKey,
      mode,
      source: reportSignupSource(campaignSource, mode),
      placement: 'final_cta',
    });
    document.getElementById('subscribe')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [campaignSource, primaryReportKey, userEmail]);

  // Check auth and load reports on mount
  useEffect(() => {
    let mounted = true;
    let loadSeq = 0;

    const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

    const waitForAuthSession = async (timeoutMs = 3500) => {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const start = Date.now();
      while (Date.now() - start < timeoutMs) {
        try {
          const session = await fetchAuthSession();
          if (session?.tokens?.idToken || session?.tokens?.accessToken) return;
        } catch {
          // ignore
        }
        await sleep(150);
      }
    };

    const loadData = async (options: { waitForSession?: boolean } = {}) => {
      const currentSeq = ++loadSeq;
      if (mounted) setAuthLoading(true);

      try {
        const reportsData = await fetchEmailReports().catch(() => [] as EmailReportPublic[]);
        const activeReports = reportsData.filter(r => r.is_active);
        let me = await getEmailFromIdToken();
        if (!me?.email && options.waitForSession) {
          await waitForAuthSession();
          me = await getEmailFromIdToken();
        }
        if (!mounted || currentSeq !== loadSeq) return;

        if (me?.email) {
          setUserEmail(me.email);

          if (process.env.NEXT_PUBLIC_API_URL) {
            apiFetchAccounts('/me').catch(() => {});
          }

          try {
            const subscriptionsData = await fetchUserSubscriptions().catch(() => [] as UserSubscription[]);

            if (!mounted) return;

            setReports(activeReports);
            setUserSubscriptions(subscriptionsData);

            setSubscriptionStates(
              subscriptionsData.reduce<Record<string, SubscriptionState>>((acc, sub) => {
                if (sub.status === 'active') {
                  acc[sub.report_key] = {
                    isLoading: false,
                    isUnsubscribing: false,
                    isSubscribed: true,
                    error: null,
                    statusMessage: null,
                    unsubscribeToken: sub.unsubscribe_token,
                  };
                }
                return acc;
              }, {})
            );
          } catch (e) {
            console.debug('Failed to load email reports:', e);
            setReports(activeReports);
            setSubscriptionStates({});
          }
        } else {
          setUserEmail(null);
          setReports(activeReports);
          setSubscriptionStates({});
          setUserSubscriptions([]);
        }
      } catch {
        if (mounted && currentSeq === loadSeq) {
          setUserEmail(null);
          setReports([]);
          setSubscriptionStates({});
          setUserSubscriptions([]);
        }
      } finally {
        if (mounted && currentSeq === loadSeq) setAuthLoading(false);
      }
    };

    loadData({ waitForSession: false });

    let unlisten: (() => void) | undefined;
    (async () => {
      try {
        const { Hub } = await import('aws-amplify/utils');
        unlisten = Hub.listen('auth', ({ payload }) => {
          const event = payload?.event;
          if (event === 'signedIn' || event === 'signedOut') {
            loadData({ waitForSession: event === 'signedIn' });
          }
        });
      } catch {
        // optional
      }
    })();

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        loadData({ waitForSession: true });
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('pageshow', handlePageShow);
    }

    return () => {
      mounted = false;
      if (unlisten) unlisten();
      if (typeof window !== 'undefined') {
        window.removeEventListener('pageshow', handlePageShow);
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadArchive = async () => {
      if (!userEmail || reports.length === 0) {
        setWeeklyArchiveItems([]);
        setWeeklyArchiveLoading(false);
        return;
      }

      const activeReportKeys = new Set(
        Object.entries(subscriptionStates)
          .filter(([, state]) => state.isSubscribed)
          .map(([reportKey]) => reportKey)
      );

      if (activeReportKeys.size === 0) {
        setWeeklyArchiveItems([]);
        setWeeklyArchiveLoading(false);
        return;
      }

      setWeeklyArchiveLoading(true);
      try {
        const items = await buildWeeklyReportArchive(
          userEmail,
          reports,
          userSubscriptions,
          REPORT_ARCHIVE_WEEKS
        );
        const reachableItems = await filterReachableArchiveItems(items);
        if (!cancelled) {
          setWeeklyArchiveItems(reachableItems.filter((item) => activeReportKeys.has(item.report_key)));
        }
      } catch {
        if (!cancelled) {
          setWeeklyArchiveItems([]);
        }
      } finally {
        if (!cancelled) {
          setWeeklyArchiveLoading(false);
        }
      }
    };

    loadArchive();

    return () => {
      cancelled = true;
    };
  }, [userEmail, reports, userSubscriptions, subscriptionStates]);

  useEffect(() => {
    if (!showHeroSubscription || weeklyArchiveLoading || weeklyArchiveItems.length === 0 || reportArchiveViewedRef.current) {
      return;
    }

    reportArchiveViewedRef.current = true;
    trackCoreJourneyEvent('reports', 'report_archive_viewed', {
      source: reportSignupSource(campaignSource, 'account'),
      placement: 'weekly_archive',
      entityType: 'report',
      entityId: weeklyArchiveItems[0]?.report_key || primaryReportKey,
      status: 'loaded',
      metadata: { mode: 'account', item_count: weeklyArchiveItems.length },
    });
  }, [campaignSource, primaryReportKey, showHeroSubscription, weeklyArchiveItems, weeklyArchiveLoading]);

  // Handle subscription
  const handleSubscribe = useCallback(async (report: EmailReportPublic, emailOverride?: string) => {
    const reportKey = report.report_key;
    const resolvedEmail = emailOverride?.trim() || undefined;
    const mode = userEmail && !resolvedEmail ? 'account' : 'public';
    const resolvedSource = reportSignupSource(campaignSource, mode);

    trackReportSignupEvent('report_signup_submit_attempted', {
      reportKey,
      mode,
      source: resolvedSource,
    });

    if (!userEmail && !resolvedEmail) {
      trackReportSignupEvent('report_signup_validation_failed', {
        reportKey,
        mode: 'public',
        source: resolvedSource,
        reason: 'missing_email',
      });
      setSubscriptionStates(prev => ({
        ...prev,
        [reportKey]: {
          isLoading: false,
          isUnsubscribing: false,
          isSubscribed: false,
          error: 'Enter your email address first.',
          statusMessage: null,
          unsubscribeToken: prev[reportKey]?.unsubscribeToken || null
        }
      }));
      return;
    }

    if (resolvedEmail && !isLikelySignupEmail(resolvedEmail)) {
      trackReportSignupEvent('report_signup_validation_failed', {
        reportKey,
        mode: 'public',
        source: resolvedSource,
        reason: 'invalid_email',
      });
      setSubscriptionStates(prev => ({
        ...prev,
        [reportKey]: {
          isLoading: false,
          isUnsubscribing: false,
          isSubscribed: false,
          error: 'Enter a valid email address.',
          statusMessage: null,
          unsubscribeToken: prev[reportKey]?.unsubscribeToken || null
        }
      }));
      return;
    }

    if (resolvedEmail) {
      setPublicStateEmail(resolvedEmail.toLowerCase());
    }

    setSubscriptionStates(prev => ({
      ...prev,
      [reportKey]: {
        isLoading: true,
        isUnsubscribing: false,
        isSubscribed: false,
        error: null,
        statusMessage: null,
        unsubscribeToken: prev[reportKey]?.unsubscribeToken || null
      }
    }));

    try {
      const response = await subscribeToEmailReport(reportKey, {
        source: resolvedSource,
        emailOverride: resolvedEmail,
      });
      const message = getSubscribeStatusMessage(response.status);
      suppressWelcomeBackPrompt('reports_signup_page_success');
      trackReportSignupEvent('report_signup_accepted', {
        reportKey,
        mode,
        source: resolvedSource,
        status: response.status,
        placement: 'reports_signup_page',
      });

      setSubscriptionStates(prev => ({
        ...prev,
        [reportKey]: {
          isLoading: false,
          isUnsubscribing: false,
          isSubscribed: true,
          error: null,
          statusMessage: message,
          unsubscribeToken: response.unsubscribe_token || prev[reportKey]?.unsubscribeToken || null
        }
      }));

      if (mode === 'account') {
        trackCoreJourneyEvent('account', 'email_preferences_updated', {
          source: 'report_signup',
          placement: 'subscription_panel',
          entityType: 'report',
          entityId: reportKey,
          status: response.status,
        });
      }
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : 'Failed to subscribe';
      trackReportSignupEvent('report_signup_failed', {
        reportKey,
        mode,
        source: resolvedSource,
        reason: classifySubscribeError(e),
      });
      setSubscriptionStates(prev => ({
        ...prev,
        [reportKey]: {
          isLoading: false,
          isUnsubscribing: false,
          isSubscribed: false,
          error: errorMessage,
          statusMessage: null,
          unsubscribeToken: prev[reportKey]?.unsubscribeToken || null
        }
      }));
    }
  }, [campaignSource, userEmail]);

  // Handle unsubscription
  const handleUnsubscribe = useCallback(async (reportKey: string, token?: string | null) => {
    trackReportSignupEvent('report_signup_unsubscribe_clicked', {
      reportKey,
      mode: 'account',
      source: reportSignupSource(campaignSource, 'account'),
      placement: 'subscription_panel',
    });
    setSubscriptionStates(prev => ({
      ...prev,
      [reportKey]: {
        ...(prev[reportKey] ?? {}),
        isUnsubscribing: true,
        error: null,
        statusMessage: null
      }
    }));

    try {
      let resolvedToken = token ?? null;

      if (!resolvedToken) {
        const subscriptions = await fetchUserSubscriptions().catch(() => [] as UserSubscription[]);
        const match = subscriptions.find(
          (sub) => sub.report_key === reportKey && sub.status === 'active' && sub.unsubscribe_token
        );
        resolvedToken = match?.unsubscribe_token ?? null;

        if (resolvedToken) {
          setSubscriptionStates(prev => ({
            ...prev,
            [reportKey]: {
              ...(prev[reportKey] ?? {}),
              unsubscribeToken: resolvedToken
            }
          }));
        }
      }

      if (!resolvedToken) {
        throw new Error('Unable to find an unsubscribe token. Please use the link in your email or refresh this page.');
      }

      await unsubscribeWithToken(resolvedToken);
      trackCoreJourneyEvent('account', 'email_preferences_updated', {
        source: 'report_signup',
        placement: 'subscription_panel',
        entityType: 'report',
        entityId: reportKey,
        status: 'unsubscribed',
      });

      setSubscriptionStates(prev => ({
        ...prev,
        [reportKey]: {
          isLoading: false,
          isUnsubscribing: false,
          isSubscribed: false,
          error: null,
          statusMessage: "You've been unsubscribed. You can resubscribe anytime.",
          unsubscribeToken: null
        }
      }));
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : 'Failed to unsubscribe';
      setSubscriptionStates(prev => ({
        ...prev,
        [reportKey]: {
          ...(prev[reportKey] ?? {}),
          isUnsubscribing: false,
          error: errorMessage
        }
      }));
    }
  }, [campaignSource]);

  const faqs = [
    {
      question: "How often will I receive reports?",
      answer: "You'll receive one curated email every week, typically on Monday mornings. We summarize the most important planning activity from the past 7 days so you can stay informed without information overload."
    },
    {
      question: "What types of applications are included?",
      answer: "Our reports cover all types of planning applications: residential developments, commercial projects, change of use, listed building consents, and more. We highlight high-impact proposals first."
    },
    {
      question: "Is there a free tier?",
      answer: "Yes. The weekly report is free, and you can subscribe with just an email address. Creating an account is optional and gives you a home for archive access and future account-based features."
    },
    {
      question: "How do I unsubscribe?",
      answer: "You can unsubscribe anytime with one click from any email, or manage your preferences in your account settings. No questions asked."
    }
  ];

  return (
    <>
      <Meta
        title="Weekly Planning Reports | City Scope"
        description="Curated weekly email summaries of local planning activity. Stay informed about high-impact developments in areas you care about."
        canonical="/reports/signup"
      />

      <div>
        <main className="planning-register min-h-screen bg-[var(--c-bg)] text-[var(--c-text-primary)]">
          <section className="mx-auto max-w-7xl px-4 pb-5 pt-[calc(var(--navbar-height,64px)+2rem)] sm:px-6 sm:pb-10 sm:pt-[calc(var(--navbar-height,64px)+3rem)] lg:px-8 lg:pt-[calc(var(--navbar-height,64px)+4rem)]">
            <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(280px,420px)] lg:items-end">
              <div>
                <p className="pr-kicker pr-kicker--accent">CityScope / Weekly Reports</p>
                <h1 className="pr-masthead mt-5 max-w-5xl">
                  Weekly <em>planning reports</em>
                </h1>
                <p className="mt-6 max-w-2xl text-base leading-7 text-[var(--c-text-secondary)] sm:text-lg">
                  {showHeroSubscription
                    ? "Here's what lands in your inbox each Monday."
                    : 'A free weekly brief on Edinburgh planning. Subscribe in seconds.'}
                </p>
              </div>

              <WeeklyStatsPanel stats={weeklyStats} className="hidden lg:block" />
            </div>
          </section>

          <section id="subscribe" className="scroll-mt-28 border-t border-[var(--c-border)]">
            <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 sm:py-12 lg:px-8">
              <div className="grid gap-5 sm:gap-6 lg:grid-cols-[0.95fr_1fr] lg:items-stretch">
                <div className="pr-report-signup-panel pr-panel flex h-full flex-col border-t-2 border-t-[var(--c-text-primary)] p-6 sm:p-8">
                  {authLoading ? (
                    <div className="flex h-full min-h-[420px] items-center justify-center">
                      <div className="flex items-center gap-3 text-[var(--c-text-secondary)]">
                        <RefreshCw className="h-5 w-5 animate-spin" />
                        <span className="pr-data">Loading…</span>
                      </div>
                    </div>
                  ) : showHeroSubscription ? (
                    <SubscribePanelLoggedIn
                      userEmail={userEmail!}
                      reports={reports}
                      subscriptionStates={subscriptionStates}
                      onSubscribe={handleSubscribe}
                      onUnsubscribe={handleUnsubscribe}
                    />
                  ) : (
                    <SubscribePanelAnonymous
                      publicEmail={publicEmail}
                      statusEmail={publicStateEmail}
                      onPublicEmailChange={setPublicEmail}
                      onEmailFocus={handleEmailFocus}
                      onEmailEntered={handleEmailEntered}
                      onEmailInvalid={handleEmailInvalid}
                      reports={reports}
                      subscriptionStates={subscriptionStates}
                      onSubscribe={handleSubscribe}
                      onCreateAccount={() => handleSignUp('anonymous_panel')}
                      onSignIn={() => handleLogin('anonymous_panel')}
                    />
                  )}
                </div>

                <WeeklyStatsPanel stats={weeklyStats} className="lg:hidden" />

                <WeeklyEmailMockup stats={weeklyStats} />
              </div>
            </div>
          </section>

          {showHeroSubscription && (weeklyArchiveLoading || weeklyArchiveItems.length > 0) ? (
            <section id="archive" className="scroll-mt-28 border-t border-[var(--c-border)]">
              <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
                <WeeklyArchiveSection items={weeklyArchiveItems} loading={weeklyArchiveLoading} />
              </div>
            </section>
          ) : null}

          <section className="border-y border-[var(--c-border)] bg-[var(--c-bg-secondary)]">
            <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
              <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="pr-section-label">What you get</p>
                  <h2 className="pr-heading mt-4">Everything you need from the weekly brief.</h2>
                </div>
                <p className="max-w-xl text-sm leading-6 text-[var(--c-text-secondary)]">
                  Our weekly reports cut through the noise and deliver exactly what matters for your area.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                <FeatureCard
                  icon={Filter}
                  title="Curated Highlights"
                  description="We filter thousands of applications to surface only the most relevant ones for you. No information overload."
                />
                <FeatureCard
                  icon={Target}
                  title="High-Impact First"
                  description="Major developments and significant changes are always prioritized at the top of your report."
                />
                <FeatureCard
                  icon={Building}
                  title="Plain-English Summaries"
                  description="AI-powered explanations that make complex planning jargon actually understandable."
                />
                <FeatureCard
                  icon={Clock}
                  title="Delivered Weekly"
                  description="One comprehensive email every Monday morning—no daily bombardment."
                />
                <FeatureCard
                  icon={Bell}
                  title="Never Miss Updates"
                  description="Important deadlines and comment periods highlighted so you can take action."
                />
              </div>
            </div>
          </section>

          <section className="mx-auto max-w-5xl px-4 py-14 sm:px-6 lg:px-8">
            <div className="mb-8 text-center">
              <p className="pr-section-label justify-center">How it works</p>
              <h2 className="pr-heading mt-4">Get started in under a minute.</h2>
              <p className="mt-4 text-sm leading-6 text-[var(--c-text-secondary)] sm:text-base">
                No credit card required.
              </p>
            </div>

            <div className="grid gap-4">
              <StepCard
                number={1}
                title="Enter your email"
                description="Subscribe in seconds with no account required. If you want saved archives and account features, you can still create an account later."
              />
              <StepCard
                number={2}
                title="Get your weekly report"
                description="Every Monday, receive a beautifully formatted email with the most important planning applications in Edinburgh."
              />
            </div>
          </section>

          <section className="border-t border-[var(--c-border)] bg-[var(--c-surface)]">
            <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6 lg:px-8">
              <div className="mb-8 text-center">
                <p className="pr-section-label justify-center">Questions</p>
                <h2 className="pr-heading mt-4">Frequently asked questions</h2>
              </div>

              <div className="space-y-3">
                {faqs.map((faq, index) => (
                  <FAQItem
                    key={index}
                    question={faq.question}
                    answer={faq.answer}
                    isOpen={openFAQ === index}
                    onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  />
                ))}
              </div>
            </div>
          </section>

          <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <div className="pr-panel grid gap-6 p-6 sm:p-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
              <div>
                <p className="pr-section-label">Stay informed</p>
                <h2 className="pr-heading mt-4">Ready to stay informed?</h2>
                <p className="mt-4 max-w-2xl text-sm leading-6 text-[var(--c-text-secondary)]">
                  Get one weekly briefing that keeps Edinburgh planning activity easy to scan.
                  Subscribe with just your email, or create an account if you want archives and account features.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <a href="#subscribe" onClick={handleFinalSubscribeCta} className="pr-button">
                  Subscribe — it&apos;s free
                  <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                </a>
                <button type="button" onClick={() => handleSignUp('final_cta')} className="pr-button pr-button--ghost">
                  Create account
                  <User className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
              <p className="lg:col-span-2 flex items-center gap-2 text-sm text-[var(--c-text-tertiary)]">
                <Shield className="h-4 w-4 text-[var(--c-accent)]" strokeWidth={1.6} />
                No spam. Unsubscribe anytime. We respect your inbox.
              </p>
            </div>
          </section>

          <footer className="border-t border-[var(--c-border)] py-10">
            <div className="mx-auto flex max-w-7xl flex-col gap-5 px-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
              <p className="text-sm text-[var(--c-text-secondary)]">
                City Scope tracks planning applications across Edinburgh, Midlothian, East Lothian and West Lothian.
              </p>
              <nav aria-label="Footer navigation" className="flex flex-wrap gap-4">
                {[
                  ['Home', '/'],
                  ['Planning Updates', '/planning-updates'],
                  ['Report archive', '/weekly-planning-reports'],
                  ['About', '/about'],
                ].map(([label, href]) => (
                  <Link key={href} href={href} className="pr-textlink">
                    {label}
                  </Link>
                ))}
              </nav>
            </div>
          </footer>
        </main>
      </div>
      <Toaster />
    </>
  );
}
